/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useCallback, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import { getWeb3Instance } from "../Connectivity/Connectivity";
import { txUrl, addressUrl } from "../Contract";
import { TOKENS, MAX_UINT256 } from "./tokens";
import { useWallet } from "./WalletContext";

/* ------------------------------------------------------------------ *
 *  Helpers
 * ------------------------------------------------------------------ */

/** Recursively turn every BigInt inside a web3 result into a string. */
export const convertBigInt = (val) => {
  if (typeof val === "bigint") return val.toString();
  if (Array.isArray(val)) return val.map(convertBigInt);
  if (typeof val === "object" && val !== null) {
    const obj = {};
    Object.keys(val).forEach((key) => {
      obj[key] = convertBigInt(val[key]);
    });
    return obj;
  }
  return val;
};

/** Pull the human readable revert reason out of any web3 / RPC error shape. */
export const extractRevertReason = (error) => {
  if (!error) return "Unknown reason";
  if (error?.data?.message) return error.data.message;
  if (error?.innerError?.message) return error.innerError.message;
  if (error?.cause?.message) return error.cause.message;
  const message = error.message || String(error);
  const match =
    message.match(/execution reverted:\s*(.*)/i) ||
    message.match(/revert\s+(.*)/i) ||
    message.match(/reason:\s*(.*)/i);
  return match ? match[1].trim() : message;
};

/** 18-decimals wei -> readable string (no scientific notation, trimmed). */
export const fromWei = (value, decimals = 18) => {
  try {
    const raw = BigInt(value ?? 0);
    const base = BigInt(10) ** BigInt(decimals);
    const whole = raw / base;
    const frac = (raw % base).toString().padStart(decimals, "0").replace(/0+$/, "");
    return frac ? `${whole}.${frac}` : whole.toString();
  } catch (_) {
    return String(value ?? "0");
  }
};

/** readable string -> wei (BigInt-safe, never uses floats). */
export const toWei = (value, decimals = 18) => {
  const str = String(value ?? "").trim();
  if (str === "") return "0";
  if (!/^\d*\.?\d*$/.test(str)) throw new Error(`"${value}" is not a valid amount`);
  const [whole = "0", frac = ""] = str.split(".");
  const padded = (frac + "0".repeat(decimals)).slice(0, decimals);
  return (BigInt(whole || "0") * BigInt(10) ** BigInt(decimals) + BigInt(padded || "0")).toString();
};

/** Format a unix timestamp coming back from the chain. */
export const formatTimestamp = (value) => {
  const seconds = Number(value || 0);
  if (!seconds) return "—";
  return new Date(seconds * 1000).toLocaleString();
};

/** Format a duration in seconds as `2d 4h 30m`. */
export const formatDuration = (value) => {
  let seconds = Number(value || 0);
  if (!seconds) return "0s";
  const d = Math.floor(seconds / 86400);
  seconds %= 86400;
  const h = Math.floor(seconds / 3600);
  seconds %= 3600;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return [d && `${d}d`, h && `${h}h`, m && `${m}m`, s && `${s}s`].filter(Boolean).join(" ");
};

const shorten = (addr) =>
  typeof addr === "string" && addr.startsWith("0x") && addr.length === 42
    ? `${addr.substring(0, 6)}...${addr.substring(38)}`
    : addr;

/** Unique cache key for a function definition (supports overloaded names). */
const keyOf = (func) => func.key || func.signature || func.name;

/**
 * web3 returns multi-value results as a `Result` object carrying BOTH numeric
 * indices and named keys plus `__length__`. Dumping that raw shows every value
 * twice, so label each ABI output once and keep the declaration order.
 */
export const describeResult = (value, outputs = []) => {
  if (value === null || value === undefined) return "—";

  if (outputs.length > 1) {
    return outputs
      .map((out, i) => {
        const raw = value?.[out.name] ?? value?.[i];
        const label = out.name || `[${i}]`;
        return `${label} (${out.type}): ${describeResult(raw, out.components || [])}`;
      })
      .join("\n");
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return "None (empty list)";
    const components = outputs[0]?.components || [];
    return value
      .map((row, i) =>
        components.length
          ? `#${i}\n${describeResult(row, components)
              .split("\n")
              .map((line) => `  ${line}`)
              .join("\n")}`
          : `#${i}: ${row}`
      )
      .join("\n");
  }

  if (typeof value === "object") {
    const entries = Object.keys(value).filter((k) => k !== "__length__" && !/^\d+$/.test(k));
    if (entries.length === 0) return JSON.stringify(value, null, 2);
    return entries.map((k) => `${k}: ${value[k]}`).join("\n");
  }

  return String(value);
};

/* ------------------------------------------------------------------ *
 *  Input coercion / validation
 * ------------------------------------------------------------------ */

const validateScalar = (type, value, label) => {
  const web3 = getWeb3Instance();
  if (type === "address") {
    if (!web3.utils.isAddress(value)) throw new Error(`${label}: invalid address`);
    return value;
  }
  if (type.startsWith("uint") || type.startsWith("int")) {
    if (!/^-?\d+$/.test(String(value).trim())) throw new Error(`${label}: must be a whole number`);
    if (type.startsWith("uint") && BigInt(value) < BigInt(0))
      throw new Error(`${label}: must be positive`);
    return String(value).trim();
  }
  if (type === "bool") {
    const v = String(value).trim().toLowerCase();
    if (!["true", "false", "1", "0"].includes(v)) throw new Error(`${label}: must be true or false`);
    return v === "true" || v === "1";
  }
  if (type.startsWith("bytes")) {
    const v = String(value).trim();
    if (!v.startsWith("0x")) throw new Error(`${label}: hex value must start with 0x`);
    return v;
  }
  return value;
};

/**
 * Turn the raw text the user typed into properly typed web3 arguments.
 * Supports: scalars, dynamic arrays, fixed arrays, tuples and `unit: "ether"` amounts.
 */
const buildArgs = (functionData, rawInputs) => {
  return (functionData.inputs || []).map((input) => {
    const baseType = input.type.replace(/\[\d*\]$/, "");

    // Fixed-size array -> one text box per slot
    if (input.isFixedArray) {
      return Array.from({ length: input.arraySize }, (_, i) => {
        const v = String(rawInputs[`${input.name}_${i}`] ?? "").trim();
        if (v === "") {
          if (input.required) throw new Error(`${input.label || input.name} [${i}] is required`);
          return baseType === "bool" ? false : "0";
        }
        const coerced = input.unit === "ether" ? toWei(v, input.decimals ?? 18) : v;
        return validateScalar(baseType, coerced, `${input.label || input.name} [${i}]`);
      });
    }

    const value = String(rawInputs[input.name] ?? "").trim();

    if (value === "") {
      if (input.required) throw new Error(`${input.label || input.name} is required`);
      if (input.type.endsWith("[]")) return [];
      if (input.type.startsWith("bytes")) return "0x";
      if (input.type === "bool") return false;
      if (input.type === "string") return "";
      return "0";
    }

    // Dynamic array — comma separated
    if (input.isArray || input.type.endsWith("[]")) {
      return value
        .split(",")
        .map((v) => v.trim())
        .filter((v) => v !== "")
        .map((v, i) => {
          const coerced = input.unit === "ether" ? toWei(v, input.decimals ?? 18) : v;
          return validateScalar(baseType, coerced, `${input.label || input.name} [${i}]`);
        });
    }

    // Struct / tuple — comma separated, one value per component
    if (input.isTuple) {
      const parts = value.split(",").map((v) => v.trim());
      const comps = input.components || [];
      if (comps.length && parts.length !== comps.length) {
        throw new Error(
          `${input.label || input.name}: expected ${comps.length} values (${comps
            .map((c) => c.name)
            .join(", ")})`
        );
      }
      return parts.map((v, i) => {
        const comp = comps[i];
        if (!comp) return v;
        const coerced = comp.unit === "ether" ? toWei(v, 18) : v;
        return validateScalar(comp.type, coerced, `${input.label || input.name}.${comp.name}`);
      });
    }

    if (input.type === "string") return value;

    const coerced = input.unit === "ether" ? toWei(value, input.decimals ?? 18) : value;
    return validateScalar(input.type, coerced, input.label || input.name);
  });
};

/* ------------------------------------------------------------------ *
 *  Component
 * ------------------------------------------------------------------ */

export default function ContractDashboard({
  title,
  subtitle,
  icon = "📡",
  accent = "#00ccff",
  address,
  abi,
  functions,
  overview = [],
  approvalTokens = [],
  extraTabs = [],
}) {
  const { account, shortAccount, connecting, connect } = useWallet();

  const [activeTab, setActiveTab] = useState("overview");
  const [loadingStates, setLoadingStates] = useState({});
  const [functionInputs, setFunctionInputs] = useState({});
  const [functionOutputs, setFunctionOutputs] = useState({});
  const [payableValues, setPayableValues] = useState({});
  const [search, setSearch] = useState("");
  const [overviewData, setOverviewData] = useState({});
  const [allowances, setAllowances] = useState({});
  const [balances, setBalances] = useState({});
  const [approveAmounts, setApproveAmounts] = useState({});
  const [isOwner, setIsOwner] = useState(false);
  const [lastTx, setLastTx] = useState(null);

  const readFunctions = useMemo(() => functions.readFunctions || [], [functions]);
  const writeFunctions = useMemo(() => functions.writeFunctions || [], [functions]);
  const adminFunctions = useMemo(() => functions.adminFunctions || [], [functions]);

  const setLoading = (key, value) =>
    setLoadingStates((prev) => ({ ...prev, [key]: value }));

  const getContract = useCallback(() => {
    const web3 = getWeb3Instance();
    return new web3.eth.Contract(abi, address);
  }, [abi, address]);

  const getTokenContract = useCallback((tokenKey) => {
    const token = TOKENS[tokenKey];
    if (!token) throw new Error(`Unknown token "${tokenKey}"`);
    const web3 = getWeb3Instance();
    return new web3.eth.Contract(token.abi, token.address);
  }, []);

  /** Resolve `contract.methods.foo` honouring explicit overload signatures. */
  const methodOf = useCallback(
    (contract, functionData, args) => {
      if (functionData.signature) return contract.methods[functionData.signature](...args);
      return contract.methods[functionData.name](...args);
    },
    []
  );

  /** The ABI entry behind a function definition — the source of truth for its outputs. */
  const abiEntryFor = useCallback(
    (functionData) => {
      const candidates = abi.filter((e) => e.type === "function" && e.name === functionData.name);
      if (candidates.length <= 1) return candidates[0];
      const canonical = (input) =>
        input.type.startsWith("tuple")
          ? `(${(input.components || []).map(canonical).join(",")})${input.type.slice(5)}`
          : input.type;
      return (
        candidates.find(
          (e) => `${e.name}(${(e.inputs || []).map(canonical).join(",")})` === functionData.signature
        ) || candidates[0]
      );
    },
    [abi]
  );

  /* ---------------------------- overview --------------------------- */

  const loadOverview = useCallback(async () => {
    if (!account || overview.length === 0) return;
    setLoading("__overview", true);
    try {
      const contract = getContract();

      const results = await Promise.all(
        overview.map(async (stat) => {
          try {
            if (stat.compute) {
              return await stat.compute({ contract, account, getTokenContract, address });
            }
            const args = (stat.args || []).map((a) => (a === "@account" ? account : a));
            const raw = await contract.methods[stat.signature || stat.method](...args).call();
            return convertBigInt(raw);
          } catch (error) {
            console.warn(`Overview "${stat.key}" failed:`, error?.message);
            return null;
          }
        })
      );

      const next = {};
      overview.forEach((stat, i) => {
        next[stat.key] = results[i];
      });
      setOverviewData(next);
    } catch (error) {
      console.error("Overview load failed:", error);
      toast.error("Failed to load contract data");
    } finally {
      setLoading("__overview", false);
    }
  }, [account, overview, getContract, getTokenContract, address]);

  /* -------------------------- token state -------------------------- */

  const loadTokenState = useCallback(async () => {
    if (!account || approvalTokens.length === 0) return;
    try {
      const entries = await Promise.all(
        approvalTokens.map(async (tokenKey) => {
          try {
            const token = getTokenContract(tokenKey);
            const [allowance, balance] = await Promise.all([
              token.methods.allowance(account, address).call(),
              token.methods.balanceOf(account).call(),
            ]);
            return [tokenKey, allowance.toString(), balance.toString()];
          } catch (error) {
            console.warn(`Token state for ${tokenKey} failed:`, error?.message);
            return [tokenKey, "0", "0"];
          }
        })
      );
      const nextAllow = {};
      const nextBal = {};
      entries.forEach(([k, a, b]) => {
        nextAllow[k] = a;
        nextBal[k] = b;
      });
      setAllowances(nextAllow);
      setBalances(nextBal);
    } catch (error) {
      console.error("Token state load failed:", error);
    }
  }, [account, approvalTokens, address, getTokenContract]);

  /* ----------------------------- owner ----------------------------- */

  const loadOwner = useCallback(async () => {
    if (!account) return;
    try {
      const owner = await getContract().methods.owner().call();
      setIsOwner(String(owner).toLowerCase() === account.toLowerCase());
    } catch (_) {
      setIsOwner(false);
    }
  }, [account, getContract]);

  const refreshAll = useCallback(() => {
    loadOverview();
    loadTokenState();
    loadOwner();
  }, [loadOverview, loadTokenState, loadOwner]);

  useEffect(() => {
    if (account) refreshAll();
    // eslint-disable-next-line
  }, [account, address]);

  /* --------------------------- approvals --------------------------- */

  const approveToken = async (tokenKey, amountWei, quiet = false) => {
    const token = getTokenContract(tokenKey);
    const gas = await token.methods
      .approve(address, amountWei)
      .estimateGas({ from: account })
      .catch(() => 120000);
    await token.methods.approve(address, amountWei).send({
      from: account,
      gas: Math.floor(Number(gas) * 1.5),
    });
    if (!quiet) toast.success(`${tokenKey} approved`);
  };

  const handleManualApprove = async (tokenKey, useMax) => {
    if (!account) return toast.error("Please connect your wallet first");
    const loadKey = `__approve_${tokenKey}`;
    setLoading(loadKey, true);
    try {
      const amountWei = useMax
        ? MAX_UINT256
        : toWei(approveAmounts[tokenKey] || "0", TOKENS[tokenKey].decimals);
      if (!useMax && amountWei === "0") throw new Error("Enter an amount to approve");
      await approveToken(tokenKey, amountWei);
      await loadTokenState();
    } catch (error) {
      console.error("Approve failed:", error);
      toast.error(extractRevertReason(error));
    } finally {
      setLoading(loadKey, false);
    }
  };

  /* ------------------------- read execution ------------------------ */

  const executeReadFunction = async (functionData) => {
    if (!account) return toast.error("Please connect your wallet first");

    const funcKey = keyOf(functionData);
    setLoading(funcKey, true);
    try {
      const contract = getContract();
      const args = buildArgs(functionData, functionInputs[funcKey] || {});

      let result = await methodOf(contract, functionData, args).call({ from: account });
      result = convertBigInt(result);

      const outputs = abiEntryFor(functionData)?.outputs || [];
      const formatted = functionData.formatOutput
        ? functionData.formatOutput(result)
        : describeResult(result, outputs);

      setFunctionOutputs((prev) => ({ ...prev, [funcKey]: formatted }));
      toast.success(`${functionData.title}: read successful`);
    } catch (error) {
      console.error("Read error:", error);
      const reason = extractRevertReason(error);
      setFunctionOutputs((prev) => ({ ...prev, [funcKey]: `❌ ${reason}` }));
      toast.error(reason);
    } finally {
      setLoading(funcKey, false);
    }
  };

  /* ------------------------ write execution ------------------------ */

  const executeWriteFunction = async (functionData) => {
    if (!account) return toast.error("Please connect your wallet first");

    const funcKey = keyOf(functionData);
    setLoading(funcKey, true);
    try {
      const contract = getContract();
      const args = buildArgs(functionData, functionInputs[funcKey] || {});

      // Native BNB attached to payable calls
      const sendOptions = { from: account };
      if (functionData.payableValue) {
        const raw = (payableValues[funcKey] || "").trim();
        if (raw !== "") sendOptions.value = toWei(raw, 18);
      }

      // 1. paused guard
      if (!functionData.allowWhilePaused) {
        try {
          const isPaused = await contract.methods.paused().call();
          if (isPaused) throw new Error("Contract is paused");
        } catch (error) {
          if (error.message === "Contract is paused") throw error;
          // contract has no paused() — carry on
        }
      }

      // 2. owner guard for owner-restricted calls
      if (functionData.ownerOnly) {
        const owner = await contract.methods.owner().call();
        if (String(owner).toLowerCase() !== account.toLowerCase()) {
          throw new Error("Only the contract owner can call this function");
        }
      }

      // 3. balance + allowance handling for paid calls
      for (const payment of functionData.payment || []) {
        const tokenKey = payment.token;
        const tokenMeta = TOKENS[tokenKey];
        const tokenContract = getTokenContract(tokenKey);

        let required = "0";
        try {
          required = String(
            await payment.amount({ contract, args, account, web3: getWeb3Instance() })
          );
        } catch (error) {
          console.warn(`Could not derive ${tokenKey} amount:`, error?.message);
          if (payment.optional) continue;
          throw new Error(`Unable to read the required ${tokenKey} amount: ${extractRevertReason(error)}`);
        }

        if (BigInt(required) === BigInt(0)) continue;

        const balance = await tokenContract.methods.balanceOf(account).call();
        if (BigInt(balance) < BigInt(required)) {
          throw new Error(
            `Insufficient ${tokenKey} balance — need ${fromWei(required, tokenMeta.decimals)}, you have ${fromWei(
              balance,
              tokenMeta.decimals
            )}`
          );
        }

        const allowance = await tokenContract.methods.allowance(account, address).call();
        if (BigInt(allowance) < BigInt(required)) {
          toast.info(`Approving ${fromWei(required, tokenMeta.decimals)} ${tokenKey}...`);
          await approveToken(tokenKey, required);
        }
      }

      // 4. simulate — surfaces the revert reason before spending gas, and gives us
      //    the function's return value, which a mined transaction does not expose.
      const outputs = abiEntryFor(functionData)?.outputs || [];
      let returnValue = null;
      try {
        const simulated = await methodOf(contract, functionData, args).call(sendOptions);
        if (outputs.length > 0) returnValue = convertBigInt(simulated);
      } catch (simError) {
        console.error("Simulation error details:", simError);
        throw new Error(`Transaction would revert: ${extractRevertReason(simError)}`);
      }

      // 5. estimate gas with a buffer
      let gasEstimate;
      try {
        gasEstimate = await methodOf(contract, functionData, args).estimateGas(sendOptions);
      } catch (estError) {
        throw new Error(`Gas estimation failed: ${extractRevertReason(estError)}`);
      }

      // 6. send
      const tx = await methodOf(contract, functionData, args).send({
        ...sendOptions,
        gas: Math.floor(Number(gasEstimate) * 1.5),
      });

      setLastTx(tx.transactionHash);

      const lines = [`✅ Confirmed in block ${tx.blockNumber}`, tx.transactionHash];
      if (returnValue !== null) {
        lines.push("", "Returned:", functionData.formatOutput
          ? String(functionData.formatOutput(returnValue))
          : describeResult(returnValue, outputs));
      }
      setFunctionOutputs((prev) => ({ ...prev, [funcKey]: lines.join("\n") }));
      toast.success(`${functionData.title} confirmed`);
      refreshAll();
    } catch (error) {
      console.error("Write error:", error);
      const reason = extractRevertReason(error);
      setFunctionOutputs((prev) => ({ ...prev, [funcKey]: `❌ ${reason}` }));
      toast.error(reason);
    } finally {
      setLoading(funcKey, false);
    }
  };

  /* ---------------------------- rendering -------------------------- */

  const handleInputChange = (funcKey, inputName, value) => {
    setFunctionInputs((prev) => ({
      ...prev,
      [funcKey]: { ...prev[funcKey], [inputName]: value },
    }));
  };

  const fillMyAddress = (funcKey, inputName) => handleInputChange(funcKey, inputName, account);

  const matchesSearch = (func) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      func.name.toLowerCase().includes(q) ||
      (func.title || "").toLowerCase().includes(q) ||
      (func.description || "").toLowerCase().includes(q) ||
      (func.category || "").toLowerCase().includes(q)
    );
  };

  const groupByCategory = (list) =>
    list.filter(matchesSearch).reduce((acc, func) => {
      const cat = func.category || "Other";
      (acc[cat] = acc[cat] || []).push(func);
      return acc;
    }, {});

  const renderInput = (funcKey, input, inputs) => {
    const commonStyle = {
      borderRadius: "12px",
      padding: "11px",
      fontSize: "0.9rem",
      background: "rgba(0,0,0,0.45)",
      border: "1px solid rgba(255,255,255,0.12)",
      color: "#fff",
    };

    if (input.isFixedArray) {
      return (
        <div className="d-flex flex-column gap-2">
          {Array.from({ length: input.arraySize }, (_, i) => (
            <input
              key={i}
              type="text"
              className="form-control"
              placeholder={`[${i}] ${input.placeholder || ""}`}
              value={inputs[`${input.name}_${i}`] || ""}
              onChange={(e) => handleInputChange(funcKey, `${input.name}_${i}`, e.target.value)}
              style={commonStyle}
            />
          ))}
        </div>
      );
    }

    if (input.type === "bool") {
      return (
        <select
          className="form-select"
          value={inputs[input.name] ?? ""}
          onChange={(e) => handleInputChange(funcKey, input.name, e.target.value)}
          style={commonStyle}
        >
          <option value="">Select…</option>
          <option value="true">true</option>
          <option value="false">false</option>
        </select>
      );
    }

    return (
      <div className="position-relative">
        <input
          type="text"
          className="form-control"
          placeholder={input.placeholder}
          value={inputs[input.name] || ""}
          onChange={(e) => handleInputChange(funcKey, input.name, e.target.value)}
          style={{ ...commonStyle, paddingRight: input.type === "address" ? "58px" : "11px" }}
        />
        {input.type === "address" && account && (
          <button
            type="button"
            onClick={() => fillMyAddress(funcKey, input.name)}
            className="btn btn-sm position-absolute top-50 end-0 translate-middle-y me-2 py-0 px-2"
            style={{
              background: "rgba(255,255,255,0.1)",
              color: accent,
              border: `1px solid ${accent}55`,
              borderRadius: "8px",
              fontSize: "0.7rem",
            }}
            title="Use my wallet address"
          >
            me
          </button>
        )}
      </div>
    );
  };

  const renderFunctionCard = (func, isWrite = false) => {
    const funcKey = keyOf(func);
    const inputs = functionInputs[funcKey] || {};
    const output = functionOutputs[funcKey];
    const isLoading = loadingStates[funcKey] || false;
    const isError = typeof output === "string" && output.startsWith("❌");
    const returns = abiEntryFor(func)?.outputs || [];

    return (
      <div key={funcKey} className="col-xl-4 col-lg-6 mb-4">
        <div
          className="card h-100 shadow-lg border-0"
          style={{
            borderRadius: "20px",
            background: "linear-gradient(135deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))",
            backdropFilter: "blur(15px)",
            border: "1px solid rgba(255,255,255,0.1)",
          }}
        >
          <div className="card-body p-4 d-flex flex-column">
            <div className="d-flex align-items-start mb-3">
              <span className="fs-3 me-3">{func.icon || (isWrite ? "📝" : "👁️")}</span>
              <div className="flex-grow-1">
                <h6 className="mb-1 text-white fw-bold">{func.title}</h6>
                <small className="text-white-50 d-block">{func.description}</small>
                <code className="d-block mt-1" style={{ fontSize: "0.7rem", color: `${accent}bb` }}>
                  {func.signature || `${func.name}()`}
                </code>
                {returns.length > 0 && (
                  <code
                    className="d-block"
                    style={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.45)" }}
                  >
                    returns {returns.map((o) => `${o.type}${o.name ? ` ${o.name}` : ""}`).join(", ")}
                  </code>
                )}
              </div>
            </div>

            {(func.inputs || []).map((input) => (
              <div key={input.name} className="mb-3">
                <label className="form-label text-white-50 small mb-1">
                  {input.label || input.name}
                  {input.required && <span className="text-danger">*</span>}
                  <span className="ms-1 opacity-50" style={{ fontSize: "0.7rem" }}>
                    ({input.type})
                  </span>
                </label>
                {renderInput(funcKey, input, inputs)}
                {(input.isArray || input.type.endsWith("[]")) && !input.isFixedArray && (
                  <small className="text-white-50 d-block mt-1" style={{ fontSize: "0.72rem" }}>
                    Separate values with commas
                  </small>
                )}
                {input.isTuple && (
                  <small className="text-white-50 d-block mt-1" style={{ fontSize: "0.72rem" }}>
                    Comma separated:{" "}
                    {(input.components || []).map((c) => c.name).join(", ")}
                  </small>
                )}
                {input.unit === "ether" && (
                  <small className="text-white-50 d-block mt-1" style={{ fontSize: "0.72rem" }}>
                    Enter a normal amount (e.g. 10.5) — converted to wei automatically
                  </small>
                )}
                {input.hint && (
                  <small className="d-block mt-1" style={{ fontSize: "0.72rem", color: `${accent}cc` }}>
                    {input.hint}
                  </small>
                )}
              </div>
            ))}

            {func.payableValue && (
              <div className="mb-3">
                <label className="form-label text-white-50 small mb-1">
                  {func.payableValue.label}
                  <span className="ms-1 opacity-50" style={{ fontSize: "0.7rem" }}>
                    (payable)
                  </span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder={func.payableValue.placeholder}
                  value={payableValues[funcKey] || ""}
                  onChange={(e) =>
                    setPayableValues((prev) => ({ ...prev, [funcKey]: e.target.value }))
                  }
                  style={{
                    borderRadius: "12px",
                    padding: "11px",
                    fontSize: "0.9rem",
                    background: "rgba(0,0,0,0.45)",
                    border: "1px solid rgba(255,193,7,0.4)",
                    color: "#fff",
                  }}
                />
                <small className="text-warning d-block mt-1" style={{ fontSize: "0.72rem" }}>
                  Native BNB attached to this transaction
                </small>
              </div>
            )}

            {func.payment && func.payment.length > 0 && (
              <div
                className="mb-3 p-2 rounded-3"
                style={{ background: "rgba(255,193,7,0.08)", border: "1px solid rgba(255,193,7,0.25)" }}
              >
                <small className="text-warning" style={{ fontSize: "0.75rem" }}>
                  💳 Requires {func.payment.map((p) => p.token).join(" + ")} — approval is handled
                  automatically
                </small>
              </div>
            )}

            <button
              onClick={() => (isWrite ? executeWriteFunction(func) : executeReadFunction(func))}
              disabled={isLoading || !account}
              className="btn w-100 mt-auto fw-bold"
              style={{
                background: isWrite
                  ? `linear-gradient(135deg, #ff6b6b, ${accent})`
                  : `linear-gradient(135deg, ${accent}, #00ff88)`,
                border: "none",
                color: "#04121a",
                borderRadius: "12px",
                padding: "11px",
                opacity: isLoading || !account ? 0.6 : 1,
              }}
            >
              {isLoading ? (
                <span>
                  <span className="spinner-border spinner-border-sm me-2" role="status" />
                  Processing…
                </span>
              ) : isWrite ? (
                "Execute"
              ) : (
                "Read"
              )}
            </button>

            {output && (
              <div
                className="mt-3 p-3 rounded-3"
                style={{
                  background: isError ? "rgba(255,107,107,0.1)" : "rgba(0,255,136,0.08)",
                  border: `1px solid ${isError ? "rgba(255,107,107,0.35)" : "rgba(0,255,136,0.3)"}`,
                  whiteSpace: "pre-wrap",
                  overflow: "auto",
                  maxHeight: "260px",
                  wordBreak: "break-all",
                }}
              >
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <small className="text-white-50">Output</small>
                  <button
                    className="btn btn-sm py-0 px-2"
                    onClick={() => {
                      navigator.clipboard?.writeText(output);
                      toast.success("Copied");
                    }}
                    style={{
                      background: "rgba(255,255,255,0.08)",
                      color: "#fff",
                      border: "none",
                      fontSize: "0.7rem",
                    }}
                  >
                    copy
                  </button>
                </div>
                <code className="text-white" style={{ fontSize: "0.8rem" }}>
                  {output}
                </code>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderFunctionList = (list, isWrite, emptyLabel) => {
    const grouped = groupByCategory(list);
    const categories = Object.keys(grouped);
    if (categories.length === 0) {
      return <div className="text-white-50 text-center py-5">{emptyLabel}</div>;
    }
    return categories.map((category) => (
      <div key={category} className="mb-5">
        <h6 className="mb-3 text-white-50 d-flex align-items-center">
          <span className="me-2 text-uppercase" style={{ letterSpacing: "1px" }}>
            {category}
          </span>
          <span className="badge rounded-pill" style={{ background: `${accent}33`, color: accent }}>
            {grouped[category].length}
          </span>
        </h6>
        <div className="row">{grouped[category].map((func) => renderFunctionCard(func, isWrite))}</div>
      </div>
    ));
  };

  const tabs = [
    { key: "overview", label: "Overview", icon: "📊", color: accent },
    { key: "read", label: `Read (${readFunctions.length})`, icon: "👁️", color: "#45b7d1" },
    { key: "write", label: `Write (${writeFunctions.length})`, icon: "📝", color: "#4ecdc4" },
    { key: "admin", label: `Admin (${adminFunctions.length})`, icon: "⚙️", color: "#ff6b6b" },
    ...extraTabs.map((t) => ({ ...t, color: t.color || "#ffaa00" })),
  ];

  /* ------------------------------ view ----------------------------- */

  return (
    <div className="container-fluid px-3 px-lg-4 pb-5">
      {/* Header */}
      <div className="text-center py-4">
        <h1
          className="fw-bold mb-2"
          style={{
            fontSize: "clamp(1.6rem, 5vw, 2.6rem)",
            background: `linear-gradient(135deg, ${accent}, #ff00aa)`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          {icon} {title}
        </h1>
        {subtitle && <p className="text-white-50 mb-2">{subtitle}</p>}
        <a
          href={addressUrl(address)}
          target="_blank"
          rel="noreferrer"
          className="text-decoration-none"
          style={{ color: `${accent}cc`, fontSize: "0.82rem" }}
        >
          {address} ↗
        </a>
      </div>

      {/* Wallet strip */}
      <div
        className="d-flex flex-wrap align-items-center justify-content-center gap-3 mb-4 p-3 rounded-4"
        style={{
          background: "rgba(255,255,255,0.04)",
          border: "1px solid rgba(255,255,255,0.1)",
        }}
      >
        {account ? (
          <>
            <span className="badge rounded-pill bg-success">Connected</span>
            <span className="text-white-50 small">🔗 {shortAccount}</span>
            {isOwner && (
              <span className="badge rounded-pill" style={{ background: "#ff6b6b", color: "#000" }}>
                👑 Owner
              </span>
            )}
            <button
              className="btn btn-sm"
              onClick={refreshAll}
              disabled={loadingStates.__overview}
              style={{
                background: `${accent}22`,
                border: `1px solid ${accent}`,
                color: accent,
                borderRadius: "10px",
              }}
            >
              {loadingStates.__overview ? "Refreshing…" : "🔄 Refresh"}
            </button>
            {lastTx && (
              <a
                href={txUrl(lastTx)}
                target="_blank"
                rel="noreferrer"
                className="small text-decoration-none"
                style={{ color: "#00ff88" }}
              >
                last tx: {shorten(lastTx)} ↗
              </a>
            )}
          </>
        ) : (
          <button
            className="btn px-4 py-2 rounded-pill fw-bold"
            onClick={connect}
            disabled={connecting}
            style={{
              background: `linear-gradient(135deg, ${accent}, #ff00aa)`,
              border: "none",
              color: "#04121a",
            }}
          >
            {connecting ? "Connecting…" : "Connect Wallet"}
          </button>
        )}
      </div>

      {!account && (
        <div className="text-center text-white-50 py-5">
          Connect your wallet on <strong>opBNB Mainnet</strong> to interact with this contract.
        </div>
      )}

      {account && (
        <div
          className="card shadow-lg border-0"
          style={{
            borderRadius: "24px",
            background: "rgba(255,255,255,0.04)",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(255,255,255,0.1)",
          }}
        >
          {/* Tabs */}
          <div className="card-header border-0 p-3 p-md-4" style={{ background: "transparent" }}>
            <ul className="nav nav-pills justify-content-center flex-wrap gap-2">
              {tabs.map((tab) => (
                <li key={tab.key} className="nav-item">
                  <button
                    className="nav-link border-0 px-3 px-md-4 py-2 fw-bold"
                    onClick={() => setActiveTab(tab.key)}
                    style={{
                      borderRadius: "14px",
                      color: activeTab === tab.key ? "#04121a" : "rgba(255,255,255,0.6)",
                      background:
                        activeTab === tab.key
                          ? `linear-gradient(135deg, ${tab.color}, ${tab.color}99)`
                          : "rgba(255,255,255,0.05)",
                      border:
                        activeTab === tab.key
                          ? `1px solid ${tab.color}`
                          : "1px solid rgba(255,255,255,0.1)",
                      transition: "all .25s ease",
                      fontSize: "0.85rem",
                    }}
                  >
                    <span className="me-1 me-md-2">{tab.icon}</span>
                    <span className="d-none d-sm-inline">{tab.label}</span>
                  </button>
                </li>
              ))}
            </ul>

            {(activeTab === "read" || activeTab === "write" || activeTab === "admin") && (
              <div className="mt-3 d-flex justify-content-center">
                <input
                  type="text"
                  className="form-control"
                  placeholder="🔍 Search functions…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{
                    maxWidth: "420px",
                    borderRadius: "14px",
                    background: "rgba(0,0,0,0.4)",
                    border: "1px solid rgba(255,255,255,0.12)",
                    color: "#fff",
                  }}
                />
              </div>
            )}
          </div>

          <div
            className="card-body p-3 p-md-4"
            style={{ background: "rgba(0,0,0,0.22)", borderRadius: "0 0 24px 24px" }}
          >
            {/* ---------------------- OVERVIEW ---------------------- */}
            {activeTab === "overview" && (
              <>
                <div className="row g-3 g-md-4">
                  {overview.map((stat) => {
                    const value = overviewData[stat.key];
                    const display =
                      value === null || value === undefined
                        ? "—"
                        : stat.format
                        ? stat.format(value)
                        : String(value);
                    const color = stat.color || accent;
                    return (
                      <div key={stat.key} className="col-xl-3 col-lg-4 col-md-6">
                        <div
                          className="card h-100 border-0"
                          style={{
                            borderRadius: "20px",
                            background: `linear-gradient(135deg, ${color}26, ${color}0d)`,
                            border: `1px solid ${color}4d`,
                            backdropFilter: "blur(10px)",
                          }}
                        >
                          <div className="card-body p-3 p-md-4 text-center">
                            <div className="fs-2 mb-2">{stat.icon}</div>
                            <h5
                              className="text-white mb-1"
                              style={{ fontSize: "clamp(0.95rem, 2.6vw, 1.25rem)", wordBreak: "break-all" }}
                            >
                              {display}
                            </h5>
                            <p className="text-white-50 mb-0 small">{stat.label}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Approvals panel */}
                {approvalTokens.length > 0 && (
                  <div className="mt-5">
                    <h6 className="text-white-50 text-uppercase mb-3" style={{ letterSpacing: "1px" }}>
                      Token Approvals
                    </h6>
                    <div className="row g-3">
                      {approvalTokens.map((tokenKey) => {
                        const token = TOKENS[tokenKey];
                        const loadKey = `__approve_${tokenKey}`;
                        const allowance = allowances[tokenKey] || "0";
                        const isUnlimited = BigInt(allowance || 0) > BigInt(10) ** BigInt(30);
                        return (
                          <div key={tokenKey} className="col-lg-6">
                            <div
                              className="card border-0 h-100"
                              style={{
                                borderRadius: "18px",
                                background: `linear-gradient(135deg, ${token.color}1f, ${token.color}0a)`,
                                border: `1px solid ${token.color}44`,
                              }}
                            >
                              <div className="card-body p-3 p-md-4">
                                <div className="d-flex align-items-center mb-3">
                                  <span className="fs-4 me-2">{token.icon}</span>
                                  <div>
                                    <div className="text-white fw-bold">{token.label}</div>
                                    <a
                                      href={addressUrl(token.address)}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="text-decoration-none"
                                      style={{ fontSize: "0.72rem", color: `${token.color}cc` }}
                                    >
                                      {shorten(token.address)} ↗
                                    </a>
                                  </div>
                                </div>
                                <div className="d-flex justify-content-between small text-white-50 mb-1">
                                  <span>Your balance</span>
                                  <span className="text-white">
                                    {fromWei(balances[tokenKey] || "0", token.decimals)} {token.symbol}
                                  </span>
                                </div>
                                <div className="d-flex justify-content-between small text-white-50 mb-3">
                                  <span>Allowance to this contract</span>
                                  <span className="text-white">
                                    {isUnlimited
                                      ? "Unlimited"
                                      : `${fromWei(allowance, token.decimals)} ${token.symbol}`}
                                  </span>
                                </div>
                                <div className="d-flex gap-2">
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Amount"
                                    value={approveAmounts[tokenKey] || ""}
                                    onChange={(e) =>
                                      setApproveAmounts((prev) => ({
                                        ...prev,
                                        [tokenKey]: e.target.value,
                                      }))
                                    }
                                    style={{
                                      borderRadius: "10px",
                                      background: "rgba(0,0,0,0.4)",
                                      border: "1px solid rgba(255,255,255,0.12)",
                                      color: "#fff",
                                    }}
                                  />
                                  <button
                                    className="btn btn-sm fw-bold"
                                    disabled={loadingStates[loadKey]}
                                    onClick={() => handleManualApprove(tokenKey, false)}
                                    style={{
                                      background: token.color,
                                      color: "#04121a",
                                      border: "none",
                                      borderRadius: "10px",
                                      minWidth: "84px",
                                    }}
                                  >
                                    {loadingStates[loadKey] ? "…" : "Approve"}
                                  </button>
                                  <button
                                    className="btn btn-sm"
                                    disabled={loadingStates[loadKey]}
                                    onClick={() => handleManualApprove(tokenKey, true)}
                                    style={{
                                      background: "transparent",
                                      color: token.color,
                                      border: `1px solid ${token.color}`,
                                      borderRadius: "10px",
                                    }}
                                  >
                                    Max
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* ----------------------- READ ------------------------ */}
            {activeTab === "read" && (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                  <h5 className="mb-0" style={{ color: "#45b7d1" }}>
                    👁️ Read Functions
                  </h5>
                  <small className="text-white-50">{readFunctions.length} available</small>
                </div>
                {renderFunctionList(readFunctions, false, "No read functions match your search.")}
              </div>
            )}

            {/* ----------------------- WRITE ----------------------- */}
            {activeTab === "write" && (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                  <h5 className="mb-0" style={{ color: "#4ecdc4" }}>
                    📝 Write Functions
                  </h5>
                  <small className="text-white-50">{writeFunctions.length} available</small>
                </div>
                {renderFunctionList(writeFunctions, true, "No write functions match your search.")}
              </div>
            )}

            {/* ----------------------- ADMIN ----------------------- */}
            {activeTab === "admin" && (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                  <h5 className="mb-0 text-danger">⚙️ Admin Functions</h5>
                  <small className="text-white-50">{adminFunctions.length} available</small>
                </div>
                <div
                  className="alert d-flex align-items-center mb-4"
                  style={{
                    background: "rgba(255,193,7,0.1)",
                    border: "1px solid rgba(255,193,7,0.35)",
                    color: "#ffc107",
                    borderRadius: "14px",
                  }}
                >
                  <span className="me-2">⚠️</span>
                  <span className="small">
                    Owner-only functions.{" "}
                    {isOwner
                      ? "You are the contract owner."
                      : "Your wallet is NOT the owner — these calls will be rejected."}
                  </span>
                </div>
                {renderFunctionList(adminFunctions, true, "No admin functions match your search.")}
              </div>
            )}

            {/* --------------------- EXTRA TABS -------------------- */}
            {extraTabs.map(
              (tab) =>
                activeTab === tab.key && (
                  <div key={tab.key}>
                    {typeof tab.render === "function"
                      ? tab.render({
                          account,
                          getContract,
                          getTokenContract,
                          refreshAll,
                          overviewData,
                          accent,
                        })
                      : null}
                  </div>
                )
            )}
          </div>
        </div>
      )}
    </div>
  );
}
